/**
 * `device-cache.md`'s **"Startup and reconnect contract"**, executed in its stated order.
 *
 * | Step | Where |
 * |---|---|
 * | 1 · load the versioned app shell | the service worker (`vite.config.ts`) |
 * | 2 · identify the account from the stored session binding | `auth/session.ts` → `start()` |
 * | 3 · render the cached gate list immediately | `#restore()` below |
 * | 4 · mark every restored status stale/checking | `statusPhaseOf` — structural, not a step |
 * | 5 · refresh the session and gate list when reception is available | `start()` + `#refresh()` |
 * | 6 · remove revoked/deleted gates, replace cloud-owned settings | `#refresh()` replaces wholesale |
 * | 7 · probe stale/unknown statuses per §6 | **removed at launch and on resume by D142** — probing is on demand: Gate Detail's mount, the app bar's refresh, D128's reconnect |
 * | 8 · reconcile completed-BLE receipts | `#reconcile()` below — **P16**, which gave this row its live path |
 * | 9 · stay offline read-only when cloud state cannot be refreshed | `readOnly` below |
 *
 * **Step 6 is a replacement, never a merge.** A gate the server stopped returning — a
 * membership removed, a gate deleted, a subscription that ended the grant — must disappear,
 * and a merge that keeps unmatched local rows is precisely how a revoked gate stays on
 * someone's Home screen. So the snapshot the response produces *is* the snapshot, and
 * `purge.ts`'s `withoutGate` exists only for the single-gate case a screen already knows about.
 *
 * **This provider renders nothing.** Home is P7's; what it consumes is `useDeviceCache()`.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { buildPath, isGatePosition, ROUTES, type GatePosition } from '@selogate/core'
import { useSession } from '../auth/context'
import { NetworkError } from '../auth/transport'
import { STAGE } from '../auth/config'
import { DeviceCacheContext, type DeviceCacheApi } from './context'
import {
  FRESH_READING_TTL_MS,
  buildSnapshot,
  gatesNeedingProbe,
  readSnapshot,
  statusPhaseOf,
  writeSnapshot,
  type CachedGate,
  type GateSnapshot,
  type Liveness,
  type SnapshotOrigin,
  type StatusPhase,
} from './snapshot'
import { reconcileReceipts } from '../ble/reconcile'
import { installResumeRefresh } from './resume'
import { openDeviceStore } from './store'

/** 🔸 Probes run a few at a time: a manager with forty gates must not open forty sockets. */
const PROBE_CONCURRENCY = 3

/**
 * The boot read's attempt schedule — **deliberately `SessionManager`'s own
 * `CONNECTIVITY_RETRY_DELAYS_MS`, the same three numbers `recoverConnectivity()` uses.**
 *
 * Not imported from there: that constant is private to the session module and bounds a *refresh*,
 * while this bounds a *gate list read*, and one of the two moving is not a reason for the other
 * to. What is shared is the shape — three attempts, front-loaded, terminating — rather than the
 * value.
 *
 * 🔸 The numbers are an assumption, as D264's are: nothing measures how long a cold launch's first
 * network call needs before it is worth asking again. Its total (4 s) is load-bearing against
 * `BootSplash`'s `MAX_HOLD_MS`, and `DeviceCacheProvider.test.tsx` asserts that inequality.
 */
export const BOOT_RETRY_DELAYS_MS = [0, 1_000, 3_000] as const

/**
 * What cancels a wait: unmount, sign-out, or a different account booting.
 *
 * `wake` is set by whoever is currently sleeping on it, and `cancelBoot` below is the only
 * writer of `cancelled` — a **push**, so a cancelled sequence stops in the same tick rather than
 * on the next poll of a clock the abort has no reason to own.
 */
interface BootAbort {
  cancelled: boolean
  wake?: (() => void) | undefined
}

/** Stop whatever the boot sequence is doing, now. Idempotent. */
function cancelBoot(abort: BootAbort): void {
  abort.cancelled = true
  abort.wake?.()
}

/**
 * A sleep the boot sequence can be woken out of.
 *
 * The timer is cleared on wake rather than merely ignored, because this provider mounts under
 * `StrictMode` and a three-second timer surviving a simulated unmount keeps a test runner awake —
 * `resume.ts`'s own teardown rule, one module over. It resolves rather than rejecting on a
 * cancel: the caller re-reads `cancelled` immediately after, so a rejection would buy a `catch`
 * and nothing else.
 */
function waitCancellably(delayMs: number, abort: BootAbort): Promise<void> {
  if (abort.cancelled) return Promise.resolve()
  return new Promise((resolve) => {
    const finish = (): void => {
      clearTimeout(timer)
      abort.wake = undefined
      resolve()
    }
    const timer = setTimeout(finish, delayMs)
    abort.wake = finish
  })
}

interface CacheState {
  readonly gates: readonly CachedGate[]
  readonly origin: SnapshotOrigin | undefined
  readonly fetchedAt?: string
  readonly refreshing: boolean
  /**
   * Whether the **last** `refresh()` failed to produce a snapshot — a non-200 or a thrown error.
   *
   * Set only once a read has run out of attempts, and **cleared the moment the next one starts**
   * rather than when it succeeds (R1-8, amended). A retry in flight is not a failure, and Home
   * reads this field to choose between *asking* and *failed*.
   */
  readonly lastRefreshFailed: boolean
  readonly probing: ReadonlySet<string>
  readonly probed: ReadonlyMap<string, Liveness>
  /**
   * When each `probed` verdict was heard, epoch ms (D146). Written and cleared in lockstep with
   * `probed` — a verdict with no age is a reading that cannot expire, which is the behavior D146
   * exists to remove.
   */
  readonly probedAt: ReadonlyMap<string, number>
  /**
   * Positions heard **in this launch**, from probes that actually reached the gate (D134).
   *
   * Kept beside `probed` and governed by the same rule for the same reason: a position restored
   * from disk is last-known, and presenting it as current is the LED bug in a second field.
   */
  readonly position: ReadonlyMap<string, GatePosition>
}

const EMPTY: CacheState = {
  gates: [],
  origin: undefined,
  refreshing: false,
  lastRefreshFailed: false,
  probing: new Set(),
  probed: new Map(),
  probedAt: new Map(),
  position: new Map(),
}

/**
 * The one ordering primitive shared by the triggers that still sweep.
 *
 * **Boot and resume no longer do (D142)** — they call `refresh()` alone, because the sweep painted
 * Home's LEDs and D141 removed them. What is left here is D128's reconnect and the app bar's
 * button: one recovery guarantee and one explicit user request. Keep the primitive rather than
 * inlining it — restoring D142 means handing it back to two call sites, not rebuilding an order.
 */
export async function runRefreshProbeCycle(
  refresh: () => Promise<void>,
  probeAll: () => Promise<void>,
): Promise<void> {
  await refresh()
  await probeAll()
}

export function DeviceCacheProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const session = useSession()
  const [cache, setCache] = useState<CacheState>(EMPTY)
  const snapshotRef = useRef<GateSnapshot | undefined>(undefined)
  const userId = session.userId
  /**
   * Whether **any** caller has landed a server snapshot in this launch. Read by the boot
   * sequence alone, to stop retrying something the reconnect effect or the app bar has already
   * done. A ref rather than `cache.fetchedAt` because the loop reads it after an `await`, where
   * a closed-over render value is the state as of the render that started the boot.
   */
  const serverFetched = useRef(false)
  /** Cleared by the sign-out branch and by unmount; see {@link waitCancellably}. */
  const bootAbort = useRef<BootAbort>({ cancelled: false })

  /**
   * **One `GET /gates`, applied to the cache — and it never claims a failure is final.**
   *
   * Split out of `refresh()` so the boot read can attempt this three times before anything
   * says so on screen. It reports its own outcome and writes **no** `lastRefreshFailed: true`
   * on a recoverable failure; the caller decides when a failure has run out of attempts. The
   * one exception is the unrecognized error below, which is a defect rather than a network
   * condition and is not made truer by two more tries.
   *
   * **R1-8 — a refresh that failed says so.** Every exit from this function still records
   * whether it produced a snapshot, because the screen above it has to tell *fetched empty*
   * from *never fetched* and could not: both non-200 and the catch used to write
   * `refreshing: false` and nothing else, and `fetchedAt` — which exists end to end — was
   * rendered nowhere. An empty list with no `fetchedAt` is "we haven't asked yet", never "you
   * have no gates".
   */
  const attemptRefresh = useCallback(
    async (owner: string): Promise<boolean> => {
      try {
        const response = await session.request({
          method: ROUTES.gateList.method,
          path: buildPath(ROUTES.gateList),
        })
        if (response.status !== 200) return false
        const raw = (response.body as { gates?: unknown }).gates
        const snapshot = buildSnapshot({
          stage: STAGE,
          userId: owner,
          fetchedAt: new Date().toISOString(),
          gates: Array.isArray(raw) ? raw : [],
        })
        snapshotRef.current = snapshot
        // Best-effort, on `rememberProfile`'s rule that **a cache is not a gate**: the response is
        // already in hand and is what the user is owed. Letting a quota eviction reject here threw
        // away a perfectly good gate list and left the screen claiming a failed refresh.
        try {
          await writeSnapshot(await openDeviceStore(), snapshot)
        } catch (error) {
          console.warn('snapshot-cache-write-failed', error)
        }
        setCache((prev) => ({
          ...prev,
          gates: snapshot.gates,
          origin: 'server',
          fetchedAt: snapshot.fetchedAt,
          refreshing: false,
          lastRefreshFailed: false,
          // A gate that is gone loses its probe result with it, rather than keeping a green
          // LED alive against a gate id the server no longer returns.
          probed: new Map(
            [...prev.probed].filter(([id]) => snapshot.gates.some((gate) => gate.gateId === id)),
          ),
          probedAt: new Map(
            [...prev.probedAt].filter(([id]) => snapshot.gates.some((gate) => gate.gateId === id)),
          ),
        }))
        serverFetched.current = true
        return true
      } catch (error) {
        // Offline: keep rendering what we have, read-only. `session.online` has already
        // flipped — the manager sets it from the same failure.
        //
        // **The state update comes before the rethrow, not after.** The reverse — the shape four
        // screen call sites still have — leaves `refreshing: true` forever on any unrecognized
        // error, which reads as a permanent spinner and logs nothing.
        if (!(error instanceof NetworkError)) {
          setCache((prev) => ({ ...prev, refreshing: false, lastRefreshFailed: true }))
          throw error
        }
        return false
      }
    },
    [session],
  )

  /**
   * **A refresh that is *running* is not a refresh that failed.**
   *
   * `lastRefreshFailed` is cleared here, at the start, and not only on the next success. It used
   * to be written `true` by a failure and left standing until a later refresh *completed*, so
   * every retry — the app bar's, reconnect's, resume's, and the boot sequence below — ran with
   * Home still rendering `loadFailedTitle` and its retry button. That is a statement about the
   * past presented as the present, on the one screen whose whole job is to say which of *never
   * asked*, *asking*, *failed* and *empty* is true. Cleared, the screen falls to the honest
   * middle branch — `fetchedAt === undefined && !lastRefreshFailed` is *asking* — for as long as
   * the request is in flight.
   *
   * **The launch screen cannot come back because of this, and the reason is arithmetic rather
   * than a guard.** `useBootSplashVisible`'s second clause settles on `lastRefreshFailed`, so
   * clearing it re-satisfies that clause — but only while its `MAX_HOLD_MS` ceiling has not
   * passed, and {@link BOOT_RETRY_DELAYS_MS} deliberately outlasts that ceiling, so nothing can
   * be in a `lastRefreshFailed === true` state early enough for a clear to matter.
   * `DeviceCacheProvider.test.tsx` asserts the inequality rather than trusting the two constants
   * to stay where they are.
   */
  const refresh = useCallback(async (): Promise<void> => {
    if (session.state !== 'signed-in' || userId === undefined) return
    setCache((prev) => ({ ...prev, refreshing: true, lastRefreshFailed: false }))
    if (!(await attemptRefresh(userId))) {
      setCache((prev) => ({ ...prev, refreshing: false, lastRefreshFailed: true }))
    }
  }, [attemptRefresh, session.state, userId])

  /**
   * **The boot read, with one bounded retry sequence — and it is not a poll.**
   *
   * The defect it removes is a flash of `home.loadFailedTitle` on a cold launch and on sign-in,
   * which then resolved into the real gate list on its own. The boot effect called `refresh()`
   * **once**; the first network call of a launch is `POST /auth/refresh`, made from inside
   * `SessionManager.request()`'s pre-send refresh, and when that call fails on the device the
   * `GET /gates` it was clearing the way for is never sent at all — `SessionUnavailableError`
   * extends `NetworkError`, so it lands in the same catch as a genuine offline and is recorded
   * as a failed gate read. From there the only route back was D264's `online` transition, which
   * D264 itself records the Android WebView not always firing, so what a resident saw was a
   * terminal-looking error that a browser event happened to clear a moment later.
   *
   * **Three attempts on {@link BOOT_RETRY_DELAYS_MS}, then it stops.** It runs once per
   * signed-in account (`bootedFor` below), it exits the moment any caller lands a server
   * snapshot, and it exits on sign-out or unmount — so the sequence terminates on its own
   * rather than becoming a background poll, which is the shape `freshnessTick`'s own timer
   * loop is written against one screen down.
   */
  const bootRefresh = useCallback(async (): Promise<void> => {
    if (session.state !== 'signed-in' || userId === undefined) return
    /**
     * **Captured once, and never re-read from the ref.** The sign-out branch cancels the current
     * abort and then *replaces* it, so a loop that re-read `bootAbort.current` after its sleep
     * would be woken by the cancel and then find a fresh, un-cancelled object — and carry on
     * reading the gates of an account that has left. That is the shape `#doRefreshLocked`'s own
     * `originalCredentials` comment describes one module over.
     */
    const abort = bootAbort.current
    setCache((prev) => ({ ...prev, refreshing: true, lastRefreshFailed: false }))
    for (const delayMs of BOOT_RETRY_DELAYS_MS) {
      if (delayMs > 0) await waitCancellably(delayMs, abort)
      // Two separate ways this loop stops early, and each is a different question. The abort is
      // *should we still be here at all*; `serverFetched` is *did somebody else already do it* —
      // the reconnect effect and the app bar share this cache, and an `online` transition landing
      // mid-sleep is exactly the overlap D128's sequence produces.
      if (abort.cancelled || serverFetched.current) return
      if (await attemptRefresh(userId)) return
    }
    // A sign-out landing during the final attempt must not leave a failure on a cache that has
    // already been emptied for the next account.
    if (abort.cancelled) return
    setCache((prev) => ({ ...prev, refreshing: false, lastRefreshFailed: true }))
  }, [attemptRefresh, session.state, userId])

  const probeGate = useCallback(
    async (gateId: string, confirmFor?: string): Promise<void> => {
      if (session.state !== 'signed-in') return
      setCache((prev) => ({ ...prev, probing: new Set(prev.probing).add(gateId) }))
      try {
        const response = await session.request({
          method: ROUTES.gateStatusRefresh.method,
          path: buildPath(ROUTES.gateStatusRefresh, { gateId }),
          // D134: the idempotency key of an open this probe is confirming. It buys the shorter
          // confirm interval and nothing else; an unusable claim is ignored server-side, so a
          // stale key costs freshness rather than the request.
          ...(confirmFor === undefined ? {} : { body: { confirmFor } }),
        })
        const evidence = probeEvidenceOf(response.status, response.body)
        const heardAt = Date.now()
        setCache((prev) => {
          const probing = new Set(prev.probing)
          probing.delete(gateId)
          if (evidence.kind === 'none') return { ...prev, probing }
          const probed = new Map(prev.probed)
          // D146: written and cleared in exactly the places `probed` is, never separately.
          const probedAt = new Map(prev.probedAt)
          const position = new Map(prev.position)
          if (evidence.kind === 'unreadable') {
            probed.delete(gateId)
            probedAt.delete(gateId)
            position.delete(gateId)
          } else {
            probed.set(gateId, evidence.liveness)
            probedAt.set(gateId, heardAt)
            // A timeout is evidence about liveness and carries no burst, so it clears the
            // position rather than leaving a stale one looking live.
            if (evidence.position === undefined) position.delete(gateId)
            else position.set(gateId, evidence.position)
          }
          return { ...prev, probing, probed, probedAt, position }
        })
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error
        // An unanswered probe is **not** an offline gate: the phone is what failed. The
        // card falls back to "last known" rather than turning red.
        setCache((prev) => {
          const probing = new Set(prev.probing)
          probing.delete(gateId)
          const probed = new Map(prev.probed)
          probed.delete(gateId)
          const probedAt = new Map(prev.probedAt)
          probedAt.delete(gateId)
          const position = new Map(prev.position)
          position.delete(gateId)
          return { ...prev, probing, probed, probedAt, position }
        })
      }
    },
    [session],
  )

  const updateGateView = useCallback(
    async (
      gateId: string,
      patch: {
        readonly alias?: string
        readonly icon?: string
        readonly color?: string
        readonly sortOrder?: number
      },
    ): Promise<void> => {
      const snapshot = snapshotRef.current
      if (snapshot === undefined) return
      const gates = snapshot.gates.map((gate) =>
        gate.gateId !== gateId
          ? gate
          : {
              ...gate,
              ...(patch.alias === undefined ? {} : { alias: patch.alias }),
              ...(patch.icon === undefined ? {} : { icon: patch.icon }),
              ...(patch.color === undefined ? {} : { color: patch.color }),
              ...(patch.sortOrder === undefined ? {} : { sortOrder: patch.sortOrder }),
            },
      )
      const next: GateSnapshot = { ...snapshot, gates }
      snapshotRef.current = next
      await writeSnapshot(await openDeviceStore(), next)
      setCache((prev) => ({ ...prev, gates: next.gates }))
    },
    [],
  )

  const probeAll = useCallback(async (): Promise<void> => {
    const snapshot = snapshotRef.current
    if (snapshot === undefined) return
    const queue = [...gatesNeedingProbe(snapshot, cache.probed, cache.probedAt)]
    const workers = Array.from({ length: Math.min(PROBE_CONCURRENCY, queue.length) }, async () => {
      for (let next = queue.shift(); next !== undefined; next = queue.shift()) {
        await probeGate(next.gateId)
      }
    })
    await Promise.all(workers)
    /**
     * **R1-11 — `ACCEPT+RECORD`: the comment is fixed, the closure is not.**
     *
     * `cache.probed` is read once, at call time, through a deliberately stale closure. The
     * justification used to name *"the boot sweep"* — **a caller D142 deleted.** What actually
     * calls this now is the app bar's refresh control and D128's reconnect sequence, and both
     * evaluate `gatesNeedingProbe` against the render's map rather than a live one, so a gate
     * that answered `online` moments earlier in the *same* sweep can be probed again.
     *
     * Left as it is on purpose. The waste is bounded by D68's limiter, nothing user-visible is
     * dishonest, and **D142's restore list depends on this code's shape** — restoring the sweep
     * means handing this primitive back to two call sites, not rebuilding it around a ref.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [probeGate])

  /**
   * **Step 8 — reconcile receipts for BLE actions that already completed.**
   *
   * P16's, and deliberately one call rather than a feature: the queue, the retry policy, and the
   * account partition all live in `web/src/ble/`. `reconcileReceipts` is single-flighted, which
   * matters here because the triggers overlap — a reconnect, a boot that finds reception, the app
   * bar's refresh, **and a BLE open's own drain** can all fire inside the same second.
   *
   * **The fourth one is not in this file**, and its absence is what the defect was: all three
   * below presume a phone that had been offline, so a BLE open on a phone that never lost
   * reception (D189(e)) waited for the next cold launch. `openLocally` drains the receipt it has
   * just written; see its rule 5.
   *
   * **It never replays an open**, and the guarantee is structural rather than a promise made
   * here: a queued receipt carries no command field, the only uploader is
   * `POST /gates/{id}/receipts`, and that route's Lambda holds no broker credential at all
   * (`infra/ble.ts`). §13's *"a network trace of the whole reconnect shows no gate command of any
   * kind"* holds on all three grounds independently.
   *
   * **Failures are swallowed on purpose.** A receipt that does not upload stays queued and is
   * retried by the next trigger; surfacing it would put an error in front of a resident about an
   * Activity row that is late rather than lost, at the exact moment their reception is worst.
   */
  const reconcile = useCallback((): void => {
    if (session.state !== 'signed-in' || userId === undefined || !session.online) return
    void (async () => {
      try {
        await reconcileReceipts(await openDeviceStore(), STAGE, userId, session.request)
      } catch (error) {
        console.warn('receipt-reconcile-failed', error)
      }
    })()
  }, [session, userId])

  // Steps 3 → 5 → 7, in order, once per signed-in account.
  const bootedFor = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (session.state === 'signed-out') {
      // Nothing of the previous account stays on screen. The durable rows are already gone —
      // `endSession()` purged them before this render.
      snapshotRef.current = undefined
      bootedFor.current = undefined
      // A bounded retry sleeping through a sign-out must not wake up and read the gates of an
      // account that has left. `serverFetched` resets with it so the next account's boot is a
      // boot rather than an early return.
      cancelBoot(bootAbort.current)
      bootAbort.current = { cancelled: false }
      serverFetched.current = false
      setCache(EMPTY)
      return
    }
    if (session.state !== 'signed-in' || userId === undefined) return
    if (bootedFor.current === userId) return
    bootedFor.current = userId

    void (async () => {
      const restored = await readSnapshot(await openDeviceStore(), STAGE, userId)
      if (restored !== undefined) {
        snapshotRef.current = restored
        setCache((prev) => ({
          ...prev,
          gates: restored.gates,
          origin: 'restored',
          fetchedAt: restored.fetchedAt,
        }))
      }
      if (!session.online) return
      /**
       * **Step 7's boot sweep is removed (D142).** It probed every stale or unknown gate on
       * launch to paint Home's LEDs; D141 took those LEDs off the tile, so the sweep spent one
       * MQTT round trip per gate — up to forty for a manager, on every cold start — to produce a
       * value no surface rendered.
       *
       * The two paths that still probe are the two that have a reader: **Gate Detail's own mount
       * probe**, which is where the LED now lives, and **the app bar's refresh control**, where a
       * user has explicitly asked. `runRefreshProbeCycle` keeps both of its halves for them and
       * for reconnect; only this call site loses the second one.
       */
      await bootRefresh()
      // Step 8, on the launch that finds reception. A receipt queued in a previous session
      // outlives the process that wrote it — that is what durable means — so a boot is exactly
      // where one is most likely to be waiting.
      reconcile()
    })()
  }, [session.state, session.online, userId, bootRefresh, reconcile])

  /**
   * The boot sequence's other exit. `waitCancellably` clears its own timer, but only if
   * something tells it to — and an unmounted provider tells it nothing otherwise.
   */
  useEffect(() => {
    const abort = bootAbort.current
    return () => cancelBoot(abort)
  }, [])

  // Reconnect: the same refresh-then-probe, when reception comes back — and step 8 with it. D128
  // fixes this sequence's *ordering* as a remediation guarantee; the reconciliation is additive
  // to it and runs alongside rather than inside, so nothing about that ordering changes.
  const wasOnline = useRef(session.online)
  useEffect(() => {
    const returned = !wasOnline.current && session.online
    wasOnline.current = session.online
    if (!returned || session.state !== 'signed-in') return
    void runRefreshProbeCycle(refresh, probeAll)
    reconcile()
  }, [session.online, session.state, refresh, probeAll, reconcile])

  /**
   * Resume: the gate-list refresh, when the app comes back to the foreground.
   *
   * **D142 narrowed this to the list, as it did boot.** The module was written against a real
   * defect — an app backgrounded for an hour returned to an hour-old list, indistinguishable from
   * a fresh one — and that half is untouched. The probe half went with Home's LEDs (D141): a
   * resident switches apps constantly, so a sweep here is the boot cost again at a higher
   * frequency, for a value no surface reads. Gate Detail probes on its own mount, and the app bar
   * still sweeps when a user asks.
   *
   * The handler is held in a ref and reassigned during render — the same pattern `HomeScreen`
   * uses for `orderRef` — so the listener is installed **once**. Putting `session`/`refresh` in
   * the effect's deps instead would re-install on every session change, and `installResumeRefresh`
   * keeps its interval floor in a closure: a re-install resets the floor, which is precisely the
   * refresh storm the floor exists to prevent.
   */
  const onResume = useRef<() => boolean>(() => false)
  onResume.current = (): boolean => {
    if (session.state !== 'signed-in' || userId === undefined) return false
    /**
     * **D264 — a latched resume asks for the connection back instead of doing nothing.**
     *
     * This used to return here, on the ground that *"offline the fetch is a `NetworkError` that
     * the `online` transition will retry properly."* The first half is true and the second is the
     * defect: the Android WebView fires `online` on a connectivity **transition**, and a phone
     * whose data was suspended mid-request (a call without VoLTE) and restored without one
     * produces none. With `session.online` false, boot returns before its read, the reconnect
     * effect below waits on a `false → true` edge nothing left running can make, and Home sends
     * every tap to the radio — so the client stayed read-only for **7h 21m** on a staging device
     * with working internet, with zero requests in the API access log across the window.
     *
     * `recoverConnectivity()` is the mechanism that was already right and simply had one caller.
     * It is bounded (0/1/3 s), single-flight, and it refuses an *online* session rather than this
     * one — so a resume cannot turn into a round trip once it has worked. On success its own
     * `false → true` transition runs the reconnect effect below, which is D128's ordering
     * unchanged; on failure nothing about the state has moved.
     *
     * **It returns `false`, so the resume floor stays unstamped**, which is `resume.ts`'s own
     * decline rule and not an oversight: what was started is a recovery attempt rather than a
     * read, and a resident who resumes in a lift and again ten seconds later in the street must
     * not be made to wait out the rest of the minute. Duplicate calls share one promise, so the
     * repetition costs nothing.
     */
    if (!session.online) {
      void session.recoverConnectivity()
      return false
    }
    // Boot has not been attempted for this account yet, and it runs this cycle itself. The test
    // is `bootedFor` rather than `snapshotRef.current !== undefined`: a first launch whose
    // `GET /gates` failed has no snapshot at all, and that is the account most in need of the
    // resume, not the one to exclude from it.
    if (bootedFor.current !== userId) return false
    void refresh()
    return true
  }
  useEffect(() => installResumeRefresh(() => onResume.current()), [])

  /**
   * The app bar's control. Deliberately the *same* primitive boot and reconnect run.
   *
   * **D146 names this control in the decision itself**, not as an implementation detail: it is
   * the user-initiated way back from a demoted reading to a verified one, so a later change that
   * narrows it to the list read alone breaks the decision rather than a convenience.
   */
  const refreshAll = useCallback(async (): Promise<void> => {
    await runRefreshProbeCycle(refresh, probeAll)
    // A user asking for a refresh is asking for everything to catch up, receipts included.
    reconcile()
  }, [refresh, probeAll, reconcile])

  /**
   * **D146's demotion, and the only moving part it needs.**
   *
   * The verdict is a comparison in `statusPhaseOf`, but a comparison nothing re-evaluates is a
   * label that never changes — the criterion is *"with the app still open on Gate Detail"*, and
   * on that screen nothing re-renders between one probe and the next. So one timer is armed for
   * the moment the **earliest live reading** expires, and firing it re-renders and nothing else.
   *
   * **It issues no request, at any point.** It addresses no controller, writes no outcome, and
   * adds nothing at launch or on resume — D142 and D141 are untouched, and a network trace across
   * the boundary is empty. Each fire drops one reading out of the set below, so the sequence
   * terminates on its own rather than becoming a poll.
   *
   * **The set below is the amendment's other half, and the half that fails silently.** An `offline`
   * reading left out of this loop would demote correctly whenever something *asked* `statusPhase()`
   * — and on Gate Detail nothing asks between one probe and the next, so the red dot would simply
   * never change. A test that only calls `statusPhase()` passes on that broken shape; the one that
   * catches it asserts the context value is a *new* object.
   */
  const [freshnessTick, setFreshnessTick] = useState(0)
  useEffect(() => {
    const now = Date.now()
    let earliest: number | undefined
    for (const [gateId, at] of cache.probedAt) {
      const verdict = cache.probed.get(gateId)
      // Both verdicts that render a present-tense claim expire (D146, amended): `online` → *מקוון*,
      // `offline` → *לא מקוון*. `unknown` already renders `stale`, so its expiry changes no label
      // and a timer for it would be a render with nothing behind it.
      if (verdict !== 'online' && verdict !== 'offline') continue
      const expiresAt = at + FRESH_READING_TTL_MS
      if (expiresAt <= now) continue
      if (earliest === undefined || expiresAt < earliest) earliest = expiresAt
    }
    if (earliest === undefined) return undefined
    const timer = setTimeout(() => setFreshnessTick((n) => n + 1), earliest - now)
    return () => clearTimeout(timer)
  }, [cache.probed, cache.probedAt, freshnessTick])

  const value = useMemo<DeviceCacheApi>(() => {
    const online = session.online
    return {
      gates: cache.gates,
      origin: cache.origin,
      ...(cache.fetchedAt === undefined ? {} : { fetchedAt: cache.fetchedAt }),
      refreshing: cache.refreshing,
      lastRefreshFailed: cache.lastRefreshFailed,
      /**
       * **Offline is read-only** (device-cache.md, and §4 of this PRD): every cloud action is
       * *unavailable*, never optimistic. A screen asks this before enabling an actuator; the
       * server's refusal remains the authority either way.
       */
      readOnly: !online,
      online,
      statusPhase: (gateId: string): StatusPhase => {
        const probed = cache.probed.get(gateId)
        const probedAt = cache.probedAt.get(gateId)
        // The verdict and its age travel together or not at all (D146) — `StatusPhaseInput`'s
        // union is what makes passing one without the other a compile error rather than a
        // reading that silently never expires.
        return statusPhaseOf(
          probed === undefined || probedAt === undefined
            ? { probing: cache.probing.has(gateId), online }
            : { probing: cache.probing.has(gateId), online, probed, probedAt },
        )
      },
      /**
       * The gate's position **as of a probe in this launch**, or `undefined` when nothing has
       * verified it. A screen that wants to show something anyway falls back to the cached
       * last-known value and must label it as such — same contract as the LED.
       */
      livePosition: (gateId: string): GatePosition | undefined => cache.position.get(gateId),
      refresh,
      refreshAll,
      probeGate,
      updateGateView,
    }
    // `freshnessTick` is in the list and in nothing else: it is what recomputes `statusPhase`
    // when a reading crosses D146's boundary with no other state having changed.
  }, [cache, session.online, freshnessTick, refresh, refreshAll, probeGate, updateGateView])

  return <DeviceCacheContext.Provider value={value}>{children}</DeviceCacheContext.Provider>
}

/**
 * What one `POST /gates/{id}/status/refresh` response is worth as evidence about the *gate*.
 *
 * `evidence` — a round trip happened and the gate either answered or did not.
 * `none` — the request succeeded but **published nothing**, so it says nothing new about the gate.
 * `unreadable` — the request failed; whatever we believed is no longer supported by anything.
 */
type ProbeEvidence =
  | {
      readonly kind: 'evidence'
      readonly liveness: Liveness
      /** The gate's reported position, when the burst carried one (D134). */
      readonly position?: GatePosition
    }
  | { readonly kind: 'none' }
  | { readonly kind: 'unreadable' }

/**
 * `POST /gates/{id}/status/refresh` → `{ gateId, status: GateStatusView, outcome, … }`.
 *
 * **Only `answered` and `timeout` are evidence (D135).** The other three outcomes return the
 * *stored* record untouched by design — `rate-limited` because D68's window was still open,
 * `deferred` because an open owns the gate lease (D125), `unavailable` because the broker could not
 * be reached at all. Reading `status.liveness` without reading `outcome` is what let a probe that
 * published nothing paint a green LED: `readGateStatus` answers `online` for any gate seen inside
 * the 60-second staleness window, so a `deferred` poll moments after an open — the single most
 * likely moment for one — reported the gate live on the strength of its own earlier ack.
 *
 * `unavailable` deliberately does not turn the gate red either. The red LED is a statement about
 * the *gate*, and a broker outage is not one (PEC-6); `stale`'s "Last known" is the honest answer,
 * and it is what leaving the previous value in place produces.
 */
function probeEvidenceOf(status: number, body: unknown): ProbeEvidence {
  if (status !== 200) return { kind: 'unreadable' }
  if (typeof body !== 'object' || body === null) return { kind: 'none' }

  const outcome = (body as { outcome?: unknown }).outcome
  if (outcome !== 'answered' && outcome !== 'timeout') return { kind: 'none' }

  const view = (body as { status?: unknown }).status
  if (typeof view !== 'object' || view === null) return { kind: 'none' }
  const liveness = (view as { liveness?: unknown }).liveness
  if (liveness !== 'online' && liveness !== 'offline' && liveness !== 'unknown') return { kind: 'none' }

  // `GateStatusView.state` is the parsed `STA:` burst. A timeout carries none, which is why the
  // position is optional on an outcome that is otherwise perfectly good evidence.
  const position = (field(view, 'state') as { position?: unknown } | undefined)?.position
  return {
    kind: 'evidence',
    liveness,
    ...(isGatePosition(position) ? { position } : {}),
  }
}

const field = (value: unknown, key: string): unknown =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>)[key] : undefined
